import React, { useCallback, useEffect, useState } from 'react';

import { Box, Checkbox, Grid } from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { forceRefreshNotificationCount } from 'actions/hellobar';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import {
  ColoredCircularProgress,
  NewStyledIconButton,
  StyledBox,
  StyledButton,
  StyledDivider,
  StyledGrid,
  StyledPaper,
  StyledTypography,
} from 'styles/common';

import {
  HELLO_BAR_PAGE_TYPE_2,
  REVIEW_FORMS_TYPE_MAP,
  REVIEW_FORM_COMPLETION_STATUS,
} from 'constants/ProjectConstants';
import { getColorFromKey } from 'constants/helper';
import { REVIEW_EVENTS1, sendReviewEvent } from 'constants/mixpanelHelper';
import {
  sendClickedBackReviewFormEvent,
  sendClickedNextReviewFormEvent,
  sendSubmittedReviewFormEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';
import { routes } from 'constants/routes';
import theme from 'constants/theme';

import { getUpdatedReviewForms, submitReview } from 'services/performanceReview';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { useReview } from '../ReviewContext';
import BehaviorForm from './BehaviorForm';
import GoalForm from './GoalForm';
import QAForm from './QAForm';
import Result from './Result';

const BlueBorderButton = styled(StyledButton)`
  border-color: ${(props) => props.theme.palette.primary.main};
  min-width: 12rem;
  width: 14rem;
  padding-top: 0.875rem;
  padding-bottom: 0.875rem;

  background-color: ${(props) => getColorFromKey(props.customColor)};
  opacity: 0.8;

  &:hover {
    opacity: 1;
    background-color: ${() => getColorFromKey('primary')};
    p {
      color: white;
    }
    svg {
      fill: white;
    }
  }
`;

const ElongatedButton = styled(StyledButton)`
  min-width: 8rem;
  width: 10rem;
  padding-top: 0.875rem;
  padding-bottom: 0.875rem;
`;

const StyledTypography1 = styled(StyledTypography)`
  position: absolute;
  padding: 0.25rem 0 0 0.25rem;
  width: 100%;
  text-align: center;
`;

const REVIEW_STAGE_COMPONENT_MAP = {
  [REVIEW_FORMS_TYPE_MAP.GOAL]: {
    id: REVIEW_FORMS_TYPE_MAP.GOAL,
    title: 'Goal Review',
    component: ({
      employeeId,
      reviewId,
      isManagerView,
      loggedInEmployeeId,
      availableReviewForms,
      isSubmitClicked,
      isShowGoalRating,
      isShowGoalWeightage,
      isGoalSectionEditable,
      refetchForm,
      isPreview,
    }) => (
      <GoalForm
        employeeId={employeeId}
        providerId={loggedInEmployeeId}
        reviewId={reviewId}
        isManagerView={isManagerView}
        availableReviewForms={availableReviewForms}
        isSubmitClicked={isSubmitClicked}
        isShowGoalWeightage={isShowGoalWeightage}
        isShowGoalRating={isShowGoalRating}
        isGoalSectionEditable={isGoalSectionEditable}
        refetchForm={refetchForm}
        isPreview={isPreview}
      />
    ),
  },
  [REVIEW_FORMS_TYPE_MAP.COMPETENCY]: {
    id: REVIEW_FORMS_TYPE_MAP.COMPETENCY,
    title: 'Competency Review',
    component: ({
      employeeId,
      reviewId,
      isManagerView,
      loggedInEmployeeId,
      isSubmitClicked,
      refetchForm,
      isPreview,
      isShowCompetencyRating,
    }) => (
      <BehaviorForm
        employeeId={employeeId}
        providerId={loggedInEmployeeId}
        reviewId={reviewId}
        isManagerView={isManagerView}
        isSubmitClicked={isSubmitClicked}
        refetchForm={refetchForm}
        isPreview={isPreview}
        isShowCompetencyRating={isShowCompetencyRating}
      />
    ),
  },
  [REVIEW_FORMS_TYPE_MAP.QNA]: {
    id: REVIEW_FORMS_TYPE_MAP.QNA,
    title: 'Overall Questions',
    component: ({
      employeeId,
      reviewId,
      isManagerView,
      loggedInEmployeeId,
      isSubmitClicked,
      refetchForm,
      isPreview,
      isShowQNARating,
    }) => (
      <QAForm
        employeeId={employeeId}
        providerId={loggedInEmployeeId}
        reviewId={reviewId}
        isManagerView={isManagerView}
        isSubmitClicked={isSubmitClicked}
        refetchForm={refetchForm}
        isPreview={isPreview}
        isShowQNARating={isShowQNARating}
      />
    ),
  },
  // result is dummy form created on UI only to show response summary after successful form submit
  RESULT: {
    id: 'RESULT',
    title: 'Response Summary',
    component: ({
      employeeId,
      reviewId,
      availableReviewForms, // this is suspected to be removed in upcoming releases
      isShowGoalWeightage,
      isShowGoalRating,
      isShowCompetencyRating,
      isShowQNARating,
      autoNavigate,
      setAutoNavigate,
    }) => (
      <Result
        employeeId={employeeId}
        reviewId={reviewId}
        availableReviewForms={availableReviewForms.map((reviewForm) => reviewForm.formName)}
        isShowGoalWeightage={isShowGoalWeightage}
        isShowGoalRating={isShowGoalRating}
        isShowCompetencyRating={isShowCompetencyRating}
        isShowQNARating={isShowQNARating}
        autoNavigate={autoNavigate}
        setAutoNavigate={setAutoNavigate}
      />
    ),
  },
};

const ReviewProgressStages = ({ selectForm, selectedForm, reviewForms, showErrorMessage, customName }) => (
  <StyledGrid container xs="12" justify="center" alignItems="center" topGutter={5} bottomGutter={5}>
    {reviewForms.map((form, index) => {
      const { formName, status, unansweredQuestions } = form;
      const { id, title } = REVIEW_STAGE_COMPONENT_MAP[formName];
      let updatedTitle = title;
      if (title.includes('Goal')) {
        updatedTitle = title.replace('Goal', customName);
      }
      const isSelectedForm = selectedForm.formName === formName;
      return (
        <React.Fragment key={id}>
          <StyledBox key={id} style={{ position: 'relative' }}>
            <BlueBorderButton
              variant="outlined"
              size="large"
              onClick={() => selectForm(form)}
              customColor={isSelectedForm ? 'primary' : 'white'}
              disableRipple
              disableFocusRipple
            >
              {status === REVIEW_FORM_COMPLETION_STATUS.IN_PROGRESS && (
                <NewStyledIconButton
                  style={{ marginRight: '4px' }}
                  padding="none"
                  size="header"
                  color={isSelectedForm ? 'white' : 'primary'}
                >
                  <WarningRoundedIcon />
                </NewStyledIconButton>
              )}
              {status === REVIEW_FORM_COMPLETION_STATUS.COMPLETED && (
                <NewStyledIconButton
                  style={{ marginRight: '4px' }}
                  padding="none"
                  size="header"
                  color={isSelectedForm ? 'white' : 'primary'}
                >
                  <CheckRoundedIcon />
                </NewStyledIconButton>
              )}
              <StyledTypography variant="body2" color={isSelectedForm ? 'white' : 'primary'} clickable>
                {updatedTitle}
              </StyledTypography>
            </BlueBorderButton>
            {showErrorMessage && unansweredQuestions !== 0 && (
              <StyledTypography1 variant="body2" color="primary">
                {`${unansweredQuestions} questions are pending`}
              </StyledTypography1>
            )}
          </StyledBox>
          {index !== reviewForms.length - 1 ? <StyledDivider length={8} thickness={0.5} color="primary" /> : null}
        </React.Fragment>
      );
    })}
  </StyledGrid>
);

const ReviewForm = ({
  reviewId,
  employeeId,
  availableReviewForms,
  isManagerView,
  isShowGoalWeightage,
  isShowRating = {},
  autoNavigate,
  setAutoNavigate,
  isGoalSectionEditable,
  isPreview,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [refetchForm, setRefetchForm] = useForceUpdate();
  const { isShowGoalRating = false, isShowCompetencyRating = false, isShowQNARating = false } = isShowRating;
  const { pageName } = useMatchedRoute();

  const {
    state: { selectedReviewInfo },
    clearPeerFeedback,
    setSelectedReviewSubmissionStatus,
  } = useReview();

  const { id: loggedInEmployeeId } = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const [reviewForms, setReviewForms] = useState(availableReviewForms);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [sendingResponse, setSendingResponse] = useState(false);
  const [selectedForm, setSelectedForm] = useState(reviewForms[0]);
  const [confirmSubmission, setConfirmSubmission] = useState(false);

  const { moduleName } = useOrganisationSettings();

  const selectForm = useCallback((form) => {
    setSelectedForm(form);
  }, []);

  // following logic needs to be changed in case of peers
  const finalForm = reviewForms[reviewForms.length - 1];

  const submitReviewForms = () => {
    setSendingResponse(true);
    submitReview({
      reviewId,
      employeeId,
    })
      .then((res) => {
        sendSubmittedReviewFormEvent({
          eventProperties: {
            reviewCycleId: reviewId,
            success: true,
          },
        });

        const { review, message } = res;
        if (review.submissionStatus === 'SUBMITTED') {
          dispatch(showSuccessSnackbar(message, 2000));
          setSelectedReviewSubmissionStatus({
            submissionStatus: 'SUBMITTED',
          });
          dispatch(forceRefreshNotificationCount({ param: HELLO_BAR_PAGE_TYPE_2.REVIEW }));
          sendReviewEvent({
            eventName: REVIEW_EVENTS1.reviewSubmitted,
            page: {
              pageName,
              flow: 'Activity',
            },
          });
        } else {
          setReviewForms(res.reviewForms);
          setRefetchForm();
          setShowErrorMessage(true);
          setIsSubmitClicked(true);
          throw new Error(message);
        }
      })
      .catch((err) => {
        console.log(err);
        sendSubmittedReviewFormEvent({
          eventProperties: {
            reviewCycleId: reviewId,
            success: false,
          },
        });
        dispatch(showErrorSnackbar(err.message || 'Form submission failed.'));
      })
      .finally(() => {
        setSendingResponse(false);
      });
  };

  const isSubmissionDisabled = isPreview
    ? false
    : selectedForm.formName === finalForm.formName &&
      (selectedReviewInfo.isSubmitted === null || selectedReviewInfo.isSubmitted || !confirmSubmission);

  useEffect(() => {
    if (selectedReviewInfo && selectedReviewInfo.isSubmitted) {
      setSelectedForm({ formName: 'RESULT' });
    }
  }, [selectedReviewInfo.isSubmitted]);

  useEffect(() => {
    setIsSubmitClicked(false);
  }, [reviewId]);

  useEffect(() => {
    clearPeerFeedback();
    if (selectedForm.formName !== 'RESULT') {
      setShowErrorMessage(false);
      if (!isPreview) {
        getUpdatedReviewForms({
          reviewId,
          employeeId,
        })
          .then((_reviewForms) => {
            setReviewForms(_reviewForms);
          })
          .catch((err) => {
            console.log('Some error occurred while fetching updated state of reviewForms');
            console.log(err);
          });
      }
    }
  }, [selectedForm]);

  const handleBackClick = () => {
    const currentFormIndex = reviewForms.map((form) => form.formName).indexOf(selectedForm.formName);
    if (currentFormIndex === 0) {
      history.push(routes.performanceReview.index);
    } else {
      sendClickedBackReviewFormEvent({
        eventProperties: {
          reviewCycleId: reviewId,
        },
      });
      setSelectedForm(reviewForms[currentFormIndex - 1]);
    }
  };

  const handleSubmitClick = () => {
    if (isPreview && selectedForm.formName === finalForm.formName) {
      dispatch(showSuccessSnackbar('This can not be submitted as it is a preview.'));
      return;
    }
    if (selectedForm.formName === finalForm.formName) {
      submitReviewForms();
    } else {
      sendClickedNextReviewFormEvent({
        eventProperties: {
          reviewCycleId: reviewId,
        },
      });
      setSelectedForm((currentForm) => {
        const currentIndex = reviewForms.map((form) => form.formName).indexOf(currentForm.formName);
        return reviewForms[currentIndex + 1];
      });
    }
  };

  return (
    <>
      {selectedReviewInfo && !selectedReviewInfo.isSubmitted && (
        <ReviewProgressStages
          selectedForm={selectedForm}
          selectForm={selectForm}
          reviewForms={reviewForms}
          showErrorMessage={showErrorMessage}
          customName={moduleName}
        />
      )}
      {!isPreview && selectedReviewInfo.isSubmitted === null && (
        <Box width="100%">
          <StyledTypography topGutter={1} bottomGutter={1} variant="body1" color="textSecondary">
            Currently, this form is uneditable. Please go back to review{' '}
            <Link to={routes.performanceReview.index}>homepage</Link> to check the status for the same.
          </StyledTypography>
        </Box>
      )}
      <Grid container>
        {selectedReviewInfo.isSubmitted
          ? REVIEW_STAGE_COMPONENT_MAP.RESULT.component({
              employeeId,
              reviewId,
              isManagerView,
              loggedInEmployeeId,
              availableReviewForms,
              isShowGoalWeightage,
              isShowGoalRating,
              isShowCompetencyRating,
              isShowQNARating,
              autoNavigate,
              setAutoNavigate,
              isGoalSectionEditable,
            })
          : REVIEW_STAGE_COMPONENT_MAP[selectedForm.formName].component({
              employeeId,
              reviewId,
              isManagerView,
              loggedInEmployeeId,
              availableReviewForms,
              isSubmitClicked,
              isShowGoalWeightage,
              isShowGoalRating,
              isShowCompetencyRating,
              isShowQNARating,
              isGoalSectionEditable,
              refetchForm,
              isPreview,
            })}
      </Grid>
      {selectedForm.formName !== 'RESULT' && selectedForm.formName === finalForm.formName && (
        <StyledPaper
          flexcontainer
          justifycontent="flex-start"
          alignitems="center"
          paddingTop={`${theme.spacing(1)}rem`}
          paddingLeft={`${theme.spacing(1)}rem`}
          paddingBottom={`${theme.spacing(1)}rem`}
          topGutter={3}
          bottomGutter={3}
        >
          <Checkbox
            color="primary"
            checked={confirmSubmission}
            onChange={() => {
              setConfirmSubmission((prev) => !prev);
            }}
          />
          <StyledTypography variant="body1" leftGutter={1}>
            I agree that I want to submit this form and will not be able to edit it later on
          </StyledTypography>
        </StyledPaper>
      )}

      {selectedForm.formName !== 'RESULT' && (
        <Box display="flex" alignItems="center" justifyContent="flex-end" marginTop="1rem" padding="1rem">
          <ElongatedButton
            variant="outlined"
            size="large"
            onClick={handleBackClick}
            customColor="white"
            disableRipple
            disableFocusRipple
            rightGutter={3}
          >
            <StyledTypography variant="body1" clickable>
              Back
            </StyledTypography>
          </ElongatedButton>

          <ElongatedButton
            variant="outlined"
            size="large"
            onClick={handleSubmitClick}
            customColor={isSubmissionDisabled ? 'white' : 'primary'}
            disableRipple
            disableFocusRipple
            disabled={isSubmissionDisabled}
          >
            {sendingResponse ? (
              <ColoredCircularProgress color="white" size="tiny" />
            ) : (
              <StyledTypography variant="body1" color={isSubmissionDisabled ? 'textSecondary' : 'white'} clickable>
                {selectedForm.formName === finalForm.formName ? 'Submit' : 'Next'}
              </StyledTypography>
            )}
          </ElongatedButton>
        </Box>
      )}
    </>
  );
};

export default ReviewForm;
